import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, XIcon } from 'lucide-react';
import { notifications, Notification } from '../data/content';
import { useIsMobile } from '../hooks/useIsMobile';

// A new notification appears every 12 seconds (visible + interval).
const CONFIG = {
  desktop: {
    initialDelay: 8000,
    visible: 4000,
    interval: 8000
  },
  mobile: {
    initialDelay: 10000,
    visible: 4000,
    interval: 8000
  }
};
function pickNext(current: number): number {
  if (notifications.length <= 1) return 0;
  let next = current;
  while (next === current) {
    next = Math.floor(Math.random() * notifications.length);
  }
  return next;
}
export function SocialProofPopup() {
  const isMobile = useIsMobile();
  const [active, setActive] = useState<Notification | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const lastIndex = useRef<number>(-1);
  const timers = useRef<number[]>([]);
  const config = isMobile ? CONFIG.mobile : CONFIG.desktop;
  const clearTimers = useCallback(() => {
    timers.current.forEach(t => window.clearTimeout(t));
    timers.current = [];
  }, []);
  const hideNow = useCallback(() => {
    setActive(null);
  }, []);
  useEffect(() => {
    if (dismissed) {
      clearTimers();
      setActive(null);
      return;
    }
    const showNext = () => {
      // Don't show while the user is typing in an input/textarea.
      const el = document.activeElement;
      const typing = el instanceof HTMLElement && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (typing) {
        const retry = window.setTimeout(showNext, 2000);
        timers.current.push(retry);
        return;
      }
      const next = pickNext(lastIndex.current);
      lastIndex.current = next;
      setActive(notifications[next]);
      const hide = window.setTimeout(() => {
        setActive(null);
        const gap = window.setTimeout(showNext, config.interval);
        timers.current.push(gap);
      }, config.visible);
      timers.current.push(hide);
    };
    const start = window.setTimeout(showNext, config.initialDelay);
    timers.current.push(start);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed, isMobile]);
  return <div className="pointer-events-none fixed bottom-4 left-3 z-50 sm:bottom-5 sm:left-5" data-id="element-192" data-mp="1vvhemnr7w43t">
      <AnimatePresence data-id="element-193" data-mp="20ukc9n4vb81r">
        {active && !dismissed && <motion.div key={active.name + active.time} initial={{
        x: '-120%',
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        opacity: 0,
        x: 0
      }} transition={{
        x: {
          duration: 0.3,
          ease: 'easeOut'
        },
        opacity: {
          duration: 0.3,
          ease: 'easeOut'
        }
      }} whileHover={{
        boxShadow: '0 14px 34px -10px rgb(var(--color-foreground) / 0.28)'
      }} onClick={hideNow} role="status" aria-live="polite" className="pointer-events-auto w-[220px] max-w-[58vw] cursor-pointer rounded-lg border-l-[3px] border-pink bg-surface px-3.5 py-2.5 shadow-toast sm:w-[280px] sm:max-w-none" data-id="element-194" data-mp="24tbcw48jl7tm">
            <button onClick={e => {
          e.stopPropagation();
          setActive(null);
        }} aria-label="Cerrar notificación" className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-foreground/40 transition-colors duration-150 hover:text-foreground/70" data-id="element-195" data-mp="35oxz4brugv5">
              <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-196" data-mp="qv141vbsy8t8" />
            </button>

            <div className="flex items-center gap-1.5 pr-4" data-id="element-197" data-mp="ludbv6bu7lxf">
              <CheckCircle2Icon className="h-3.5 w-3.5 shrink-0 text-pink" aria-hidden="true" data-id="element-198" data-mp="gwk9yphkhe2y" />
              <span className="text-[11px] font-bold text-foreground" data-id="element-199" data-mp="36lm6l8uabgrd">Compra verificada</span>
            </div>
            <p className="mt-1 text-[11px] leading-snug text-foreground" data-id="element-200" data-mp="31m1ad66i1mic">
              <span className="font-semibold" data-id="element-201" data-mp="31ovu0vhov3ap">{active.name}</span> — {active.location}
            </p>
            <p className="mt-0.5 text-[11px] leading-snug text-foreground" data-id="element-202" data-mp="u9hvbbi7pt7b">
              adquirió <span className="font-semibold text-pink" data-id="element-203" data-mp="z45klcl1irmy">“Coreano Fácil”</span>
            </p>
            <p className="mt-0.5 text-[10px] text-muted" data-id="element-204" data-mp="kezj1hyqcpbw">{active.time}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}